import { type DragEndEvent } from "@dnd-kit/core";
import { arrayMove } from "@dnd-kit/sortable";
import { type ColumnOrderState, type Updater, type ColumnDef } from "@tanstack/react-table";
import { useLocalStorage } from "react-use";

export function useTableColumnOrder<TData>(tableId: string, columns: Array<ColumnDef<TData, unknown>>) {
  const defaultOrder = columns.map((c) => c.id!);
  const [columnOrder, setColumnOrder] = useLocalStorage(`table-order-${tableId}`, defaultOrder);

  const validatedColumnOrder = (): string[] => {
    const currentOrder = columnOrder || [];
    const columnIds = new Set(defaultOrder);

    // Filter out any saved columns that no longer exist
    const validColumns = currentOrder.filter((id) => columnIds.has(id));

    // Add any new columns that aren't in the saved order
    const newColumns = defaultOrder.filter((id) => !currentOrder.includes(id));

    return [...validColumns, ...newColumns];
  };

  const onColumnOrderChange = (updater: Updater<ColumnOrderState>) => {
    const updatedColumnOrder = typeof updater === "function" ? updater(validatedColumnOrder()) : updater;
    setColumnOrder(updatedColumnOrder);
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (active && over && active.id !== over.id) {
      onColumnOrderChange((prevColumnOrder) => {
        const oldIndex = prevColumnOrder.indexOf(active.id.toString());
        const newIndex = prevColumnOrder.indexOf(over.id.toString());
        return arrayMove(prevColumnOrder, oldIndex, newIndex);
      });
    }
  };

  return {
    columnOrder: validatedColumnOrder(),
    onColumnOrderChange,
    handleDragEnd,
  };
}
